/**
 * 点云几何与渲染工具函数
 */
import * as THREE from 'three'

/**
 * 计算变换后的包围盒。
 *
 * @param box - 原始 AABB
 * @param transform - 作用于 8 个顶点的变换矩阵
 * @returns 新的世界空间 AABB
 */
export function computeTransformedBoundingBox(box: THREE.Box3, transform: THREE.Matrix4) {
  const vertices = [
    new THREE.Vector3(box.min.x, box.min.y, box.min.z).applyMatrix4(transform),
    new THREE.Vector3(box.min.x, box.min.y, box.min.z).applyMatrix4(transform),
    new THREE.Vector3(box.max.x, box.min.y, box.min.z).applyMatrix4(transform),
    new THREE.Vector3(box.min.x, box.max.y, box.min.z).applyMatrix4(transform),
    new THREE.Vector3(box.min.x, box.min.y, box.max.z).applyMatrix4(transform),
    new THREE.Vector3(box.min.x, box.max.y, box.max.z).applyMatrix4(transform),
    new THREE.Vector3(box.max.x, box.max.y, box.min.z).applyMatrix4(transform),
    new THREE.Vector3(box.max.x, box.min.y, box.max.z).applyMatrix4(transform),
    new THREE.Vector3(box.max.x, box.max.y, box.max.z).applyMatrix4(transform),
  ]

  const boundingBox = new THREE.Box3()
  boundingBox.setFromPoints(vertices)
  return boundingBox
}

/**
 * 生成指定颜色的 DataTexture。
 *
 * @param width - 纹理宽度，像素
 * @param height - 纹理高度，像素
 * @param color - 填充色
 * @returns 采用 RGBA、NearestFilter 的 DataTexture
 */
export function generateDataTexture(width: number, height: number, color: THREE.Color) {
  const size = width * height
  const data = new Uint8Array(4 * width * height)

  const r = Math.floor(color.r * 255)
  const g = Math.floor(color.g * 255)
  const b = Math.floor(color.b * 255)

  for (let i = 0; i < size; i++) {
    data[i * 4] = r
    data[i * 4 + 1] = g
    data[i * 4 + 2] = b
    data[i * 4 + 3] = 255
  }

  const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat)
  texture.needsUpdate = true
  texture.minFilter = THREE.NearestFilter
  texture.magFilter = THREE.NearestFilter

  return texture
}

/**
 * 创建满足节点数量需求的 DataTexture（考虑 GPU 最大纹理尺寸限制）。
 *
 * @param requiredNodeCount - 需容纳的节点数
 * @param maxTextureSize - 设备最大纹理尺寸，来自 renderer.capabilities.maxTextureSize
 * @param fillColor - 初始填充色，默认白色
 * @returns 成功时返回 `{ success: true, texture }`，否则返回 `{ success: false, message }`
 */
export function resizeVisibleNodesTexture(
  requiredNodeCount: number,
  maxTextureSize: number,
  fillColor = new THREE.Color(0xffffff)
) {
  const preferredWidth = 2048
  let width = Math.min(preferredWidth, maxTextureSize)
  let height = Math.ceil(requiredNodeCount / width)
  width *= height
  height = 1

  if (height > maxTextureSize) {
    return {
      success: false,
      message:
        `Required node count (${requiredNodeCount}) would require texture height of ${height}, ` +
        `but GPU only supports up to ${maxTextureSize} (width: ${width}).`,
    }
  }

  const size = width * height
  const data = new Uint8Array(4 * size)

  const r = Math.floor(fillColor.r * 255)
  const g = Math.floor(fillColor.g * 255)
  const b = Math.floor(fillColor.b * 255)
  const a = 255

  for (let i = 0; i < size; i++) {
    const idx = i * 4
    data[idx] = r
    data[idx + 1] = g
    data[idx + 2] = b
    data[idx + 3] = a
  }

  const texture = new THREE.DataTexture(
    data,
    width,
    height,
    THREE.RGBAFormat,
    THREE.UnsignedByteType
  )
  texture.needsUpdate = true
  texture.magFilter = THREE.NearestFilter
  texture.minFilter = THREE.NearestFilter

  return { success: true, texture }
}

/**
 * 根据八叉树索引创建子 AABB。
 *
 * @param aabb - 父节点 AABB
 * @param index - 子节点索引，0-7（低三位分别对应 x/y/z）
 * @returns 对应子节点的 AABB
 */
export function createChildAABB(aabb: THREE.Box3, index: number) {
  const min = aabb.min.clone()
  const max = aabb.max.clone()
  const size = new THREE.Vector3().subVectors(max, min)

  if ((index & 0b0001) > 0) {
    min.z += size.z / 2
  } else {
    max.z -= size.z / 2
  }
  if ((index & 0b0010) > 0) {
    min.y += size.y / 2
  } else {
    max.y -= size.y / 2
  }
  if ((index & 0b0100) > 0) {
    min.x += size.x / 2
  } else {
    max.x -= size.x / 2
  }

  return new THREE.Box3(min, max)
}

/** 创建无内部对角边的全屏覆盖三角形。 */
function createFullscreenTriangleGeometry(): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute([-1, -1, 0, 3, -1, 0, -1, 3, 0], 3)
  )
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 2, 0, 0, 2], 2))
  return geometry
}

interface ScreenPassResources {
  scene: THREE.Scene
  quad: THREE.Mesh<THREE.BufferGeometry, THREE.Material>
  camera: THREE.Camera
  owners: number
}

/** 按 Renderer 隔离全屏 pass 资源，避免模块级 Geometry 反向保活历史 Context。 */
class ScreenPass {
  private readonly resourcesByRenderer = new WeakMap<THREE.WebGLRenderer, ScreenPassResources>()

  retain(renderer: THREE.WebGLRenderer): void {
    this.getResources(renderer).owners++
  }

  release(renderer: THREE.WebGLRenderer): void {
    const resources = this.resourcesByRenderer.get(renderer)
    if (!resources) {
      return
    }
    resources.owners = Math.max(resources.owners - 1, 0)
    if (resources.owners === 0) {
      this.dispose(renderer)
    }
  }

  render(
    renderer: THREE.WebGLRenderer,
    material: THREE.Material,
    target?: THREE.WebGLRenderTarget | null,
    viewport?: { x: number; y: number; width: number; height: number }
  ): void {
    const resources = this.getResources(renderer)
    resources.quad.material = material
    renderer.setRenderTarget(target ?? null)
    if (viewport) {
      renderer.setViewport(viewport.x, viewport.y, viewport.width, viewport.height)
      renderer.setScissor(viewport.x, viewport.y, viewport.width, viewport.height)
      renderer.setScissorTest(true)
    }
    renderer.render(resources.scene, resources.camera)
  }

  /** 返回指定 Renderer 的 Geometry，仅供生命周期测试与诊断。 */
  getGeometryForDiagnostics(renderer: THREE.WebGLRenderer): THREE.BufferGeometry {
    return this.getResources(renderer).quad.geometry
  }

  /** 立即释放指定 Renderer 的全屏 pass 资源。 */
  dispose(renderer: THREE.WebGLRenderer): void {
    const resources = this.resourcesByRenderer.get(renderer)
    if (!resources) {
      return
    }
    resources.quad.geometry.dispose()
    resources.quad.material = null as unknown as THREE.Material
    resources.scene.clear()
    this.resourcesByRenderer.delete(renderer)
  }

  private getResources(renderer: THREE.WebGLRenderer): ScreenPassResources {
    const existing = this.resourcesByRenderer.get(renderer)
    if (existing) {
      return existing
    }
    const scene = new THREE.Scene()
    const quad = new THREE.Mesh<THREE.BufferGeometry, THREE.Material>(
      createFullscreenTriangleGeometry(),
      null as unknown as THREE.Material
    )
    const resources = { scene, quad, camera: new THREE.Camera(), owners: 0 }
    scene.add(quad)
    this.resourcesByRenderer.set(renderer, resources)
    return resources
  }
}

/** 全屏 pass 渲染工具（用于 EDL / Normalization 后处理）。 */
export const screenPass = new ScreenPass()
